import { ApplicationTypes, type EnhancedDomain, Model } from 'awr-domain'
import { match } from 'ts-pattern'

import {
  NavigationUpdateTag,
  type Rectangle,
  type StateUpdate,
  domainEntityTypeFromId,
  isInitialNavigationUpdate,
  isSelectUpdate,
  isToolUpdate,
  traverseFromShape,
} from '@salsita-ai/platform-project-sdk/domain'

import { configuratorInterface } from './configurator-interface'

export const deselectOnNavigation = configuratorInterface.transformerBuilder.when
  .navigated()
  .then.deselect()
  .build('deselect-on-navigation')

export const deselectOnToolStateChange = configuratorInterface.transformerBuilder.when
  .update(['modifyEphemeralRecord'])
  .fired.withTag([NavigationUpdateTag.ActivateTool])
  .then.deselect()
  .build('deselect-on-tool-state-change')

export const changeViewOnToolStateChange = configuratorInterface.transformerBuilder.when
  .update(['modifyEphemeralRecord'])
  .fired.withTag([NavigationUpdateTag.ActivateTool])
  .then.applySideEffect(ctx => {
    const update = ctx.update

    if (!isToolUpdate(update)) {
      return []
    }

    if (update.writableState.activeState === ApplicationTypes.ToolState.Sketching) {
      return [ctx.domainContext.navigationSystem.getViewUpdate(ApplicationTypes.ViewState.FloorPlan)]
    }

    return []
  })
  .build('change-view-on-tool-state-change')

export const changeViewOnBuildNavigation = configuratorInterface.transformerBuilder.when
  .navigated({ to: [ApplicationTypes.NavigationState.Build] })
  .then.changeView(ApplicationTypes.ViewState.FloorPlan)
  .build('change-view-on-build-navigation')

export const changeViewOnPropertiesNavigation = configuratorInterface.transformerBuilder.when
  .navigated({ to: [ApplicationTypes.NavigationState.Properties] })
  .then.changeView(ApplicationTypes.ViewState['3dView'])
  .build('change-view-on-properties-navigation')

export const resetThinIntermediatePostsOnMetalPostType = configuratorInterface.transformerBuilder.when
  .entity(['Deck'])
  .modified({ atFields: ['postType'] })
  .causes.entity(['Deck'])
  .violation(['noIntermediateWithRoundPosts'])
  .then.applySideEffect(ctx => {
    return [
      configuratorInterface.updates.DomainEntityModification({
        entityId: ctx.entityId,
        stateDelta: {
          useThinIntermediatePosts: false,
        },
        updateTag: 'fix.noIntermediateWithRoundPosts',
      }),
    ]
  })
  .build('reset-thin-intermediate-posts-on-metal-post-type')

export const resetThinIntermediatePostsOnRoundPostStyle = configuratorInterface.transformerBuilder.when
  .entity(['Deck'])
  .modified({ atFields: ['postStyle'] })
  .causes.entity(['Deck'])
  .violation(['noIntermediateWithRoundPosts'])
  .then.applySideEffect(ctx => {
    return [
      configuratorInterface.updates.DomainEntityModification({
        entityId: ctx.entityId,
        stateDelta: {
          useThinIntermediatePosts: false,
        },
        updateTag: 'fix.noIntermediateWithRoundPosts',
      }),
    ]
  })
  .build('reset-thin-intermediate-posts-on-round-post-style')

export const resetHandrailTypeOnIncompatiblePostStyle = configuratorInterface.transformerBuilder.when
  .entity(['Deck'])
  .modified({ atFields: ['postStyle'] })
  .causes.entity(['Deck'])
  .violation(['stainlessHandrail'])
  .then.applySideEffect(ctx => {
    return [
      configuratorInterface.updates.DomainEntityModification({
        entityId: ctx.entityId,
        stateDelta: {
          handrailType: Model.HandrailType.FlatTimber,
        },
        updateTag: 'fix.stainlessHandrail',
      }),
    ]
  })
  .build('reset-handrail-type-on-incompatible-post-style')

export const resetFreeWallEndTypeOnPossibleWallFreeEndType = configuratorInterface.transformerBuilder.when
  .entity(['HandrailEnd'])
  .modified({ atFields: ['possibleWallEndType'] })
  .then.applySideEffect(ctx => {
    return [
      configuratorInterface.updates.DomainEntityModification({
        entityId: ctx.entityId,
        stateDelta: {
          endType:
            ctx.update.stateDelta.possibleWallEndType === Model.HandrailPossibleWallEndType.Free
              ? Model.HandrailEndType.RadiusedEndCap
              : Model.HandrailEndType.OblongWallPlate,
        },
        updateTag: 'fix.resetFreeWallEndType',
      }),
    ]
  })
  .build('reset-free-wall-end-type-on-possible-wall-free-end-type')

const handleProjection = (
  new$root: EnhancedDomain.$RootEntity,
  current$root: EnhancedDomain.$RootEntity,
  entities: readonly Model.AnyDomainEntity[],
  pendingUpdates: ReadonlyArray<StateUpdate<Model.AnyDomainEntity, Model.AnyDomainEntity>>
) => {
  const sequence = configuratorInterface.getIdSequence(entities, [])

  const newKeys = new Set<string>(new$root.$items.flatMap(item => item.$projectedEntityRecipes.map(e => e.key)))
  const currentEntities = new Map<string, EnhancedDomain.$ProjectedEntity['id']>(
    current$root.$items.flatMap(item => item.$projectedEntities.map(e => [e.key, e.id]))
  )

  const deletedEntities = current$root.$items.flatMap(item => {
    return item.$projectedEntities.flatMap(e => {
      if (newKeys.has(e.key) || pendingUpdates.some(u => u.kind === 'deleteEntity' && u.entityId === e.id)) {
        return []
      }

      return [
        configuratorInterface.updates.EntityDeletion({
          entityId: e.id,
          updateTag: 'projectedEntity.delete',
        }),
      ]
    })
  })

  const otherUpdates = new$root.$items.flatMap(e => {
    return e.$projectedEntityRecipes.map(p => {
      const currentId = currentEntities.get(p.key)

      if (currentId) {
        return configuratorInterface.updates.DomainEntityModification({
          entityId: currentId,
          stateDelta: { ...p, id: currentId },
          updateTag: 'projectedEntity.update',
        })
      }

      return configuratorInterface.updates.DomainEntityCreation({
        entity: {
          ...p,
          id: sequence.nextId(p.type),
        } as EnhancedDomain.$ProjectedEntity,
        updateTag: 'projectedEntity.create',
        parentId: p.deckItemId,
      })
    })
  })

  return [...deletedEntities, ...otherUpdates]
}

export const keepProjectedEntities = configuratorInterface.transformerBuilder.always.then
  .applySideEffect(ctx => {
    const expressionContext = ctx.domainContext.domain.getExpressionContext()

    if (expressionContext.mode === 'read') {
      return []
    }

    if (
      !ctx.updates.some(u => {
        return (
          isInitialNavigationUpdate(u) ||
          configuratorInterface.isCreateEntityUpdate(u, ['DeckSection', 'Stairs']) ||
          configuratorInterface.isDeleteEntityUpdate(u, ['DeckSection', 'Stairs']) ||
          (u.kind === 'modifyDomainEntity' && domainEntityTypeFromId(u.entityId) === 'Stairs') ||
          (u.kind === 'modifyDomainEntity' && domainEntityTypeFromId(u.entityId) === 'DeckSection') ||
          (u.kind === 'modifyDomainEntity' && domainEntityTypeFromId(u.entityId) === 'Deck')
        )
      })
    ) {
      return []
    }

    const $root = ctx.getNew$Root()
    const current$root = ctx.getOld$Root()
    const entities = ctx.getNewState().entities

    const updates = handleProjection($root, current$root, entities, ctx.updates)

    return updates
  })
  .build('keep-projected-entities')

export const revertBuiltInAutoSelection = configuratorInterface.transformerBuilder.when
  .entity(Model.AnyDomainEntity.types)
  .created()
  .then.overrideAllUpdates(ctx => ctx.updates.filter(update => !isSelectUpdate(update)))
  .build('revert-built-in-auto-selection')

const getSketchingDepthUpdates = (rectangle: Rectangle, diffX: number, diffY: number) => {
  const updates: Array<StateUpdate<Model.AnyDomainEntity, Model.AnyDomainEntity>> = []

  const visited = new Set<string>([rectangle.key])

  const connectedShapes = [rectangle.bottomLeft.connection?.to.shape, rectangle.bottomRight.connection?.from.shape]

  for (const scheduledShape of connectedShapes) {
    if (!scheduledShape) {
      continue
    }

    traverseFromShape(
      scheduledShape,
      shape => {
        if (shape.type === 'line') {
          const update = configuratorInterface.updates.ComponentModification({
            componentInstance: shape.instance,
            updateTag: 'SketchingPoint.update',
            writableState: {
              x: shape.start.x + diffX,
              y: shape.start.y + diffY,
              angle: shape.angle,
              length: shape.length,
            },
          })

          updates.push(update)
        } else if (shape.type === 'rectangle') {
          const update = configuratorInterface.updates.ComponentModification({
            componentInstance: shape.instance,
            updateTag: 'SketchingPoint.update',
            writableState: {
              x: shape.topLeft.x + diffX,
              y: shape.topLeft.y + diffY,
              angle: shape.angle,
              width: shape.width,
              depth: shape.depth,
            },
          })

          updates.push(update)
        }
      },
      visited
    )
  }

  return updates
}

export const moveSketchingWhenUpdatingNumberOfSteps = configuratorInterface.transformerBuilder.when
  .entity(['Stairs'])
  .modified({ atFields: ['numberOfSteps'] })
  .then.applySideEffect(ctx => {
    const oldState = ctx.getOldState()
    const oldEntity = ctx.getOld$Entity()

    const rectangle = ctx.domainContext.sketchingProjections.getEntityRectangle(ctx.update.entityId, oldState, 'main')

    const newDepth = (ctx.update.stateDelta.numberOfSteps - 1) * oldEntity.goingDistance

    const diffX = (rectangle.depth - newDepth) * Math.sin(rectangle.angle)
    const diffY = (newDepth - rectangle.depth) * Math.cos(rectangle.angle)

    const updates = getSketchingDepthUpdates(rectangle, diffX, diffY)

    return updates
  })
  .build('move-sketching-when-updating-number-of-steps')

export const moveSketchingWhenUpdatingGoingDistance = configuratorInterface.transformerBuilder.when
  .entity(['Stairs'])
  .modified({ atFields: ['goingDistance'] })
  .then.applySideEffect(ctx => {
    const oldState = ctx.getOldState()
    const oldEntity = ctx.getOld$Entity()

    const rectangle = ctx.domainContext.sketchingProjections.getEntityRectangle(ctx.update.entityId, oldState, 'main')

    const newDepth = (oldEntity.numberOfSteps - 1) * ctx.update.stateDelta.goingDistance

    const diffX = (rectangle.depth - newDepth) * Math.sin(rectangle.angle)
    const diffY = (newDepth - rectangle.depth) * Math.cos(rectangle.angle)

    const updates = getSketchingDepthUpdates(rectangle, diffX, diffY)

    return updates
  })
  .build('move-sketching-when-updating-going-distance')

export const resetFixingSystemOnPostTypeChange = configuratorInterface.transformerBuilder.when
  .entity(['Deck'])
  .modified({ atFields: ['postType'] })
  .then.applySideEffect(ctx => {
    const newValue = ctx.update.stateDelta.postType

    const defaults = match(newValue)
      .with(Model.PostType.Metal, () => {
        return {
          fixingSystem: Model.FixingSystemType.ToggleLock,
          useThinIntermediatePosts: false,
          postStyle: Model.MetalPostStyleType.RoundStainless,
          handrailType: Model.HandrailType.FlatTimber,
        }
      })
      .with(Model.PostType.Timber, () => {
        return {
          fixingSystem: Model.FixingSystemType.ForkWithSaddles,
          useThinIntermediatePosts: false,
        }
      })
      .exhaustive()

    return [
      configuratorInterface.updates.DomainEntityModification({
        entityId: ctx.update.entityId,
        stateDelta: defaults,
        updateTag: 'fix.resetFixingSystem',
      }),
    ]
  })
  .build('reset-fixing-system-on-post-type-change')

export const fixFixingSystemOnPostStyleChange = configuratorInterface.transformerBuilder.when
  .entity(['Deck'])
  .modified({ atFields: ['postStyle'] })
  .causes.entity(['Deck'])
  .violation(['no45DegToggleLock'])
  .then.applySideEffect(ctx => {
    return [
      configuratorInterface.updates.DomainEntityModification({
        entityId: ctx.update.entityId,
        stateDelta: {
          fixingSystem: Model.FixingSystemType.MetalInsert,
        },
        updateTag: 'fix.fixFixingSystem',
      }),
    ]
  })
  .build('fix-fixing-system-on-post-style-change')
